import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useZoom } from 'context/ZoomContext';
import { PrismThemeProvider } from 'theme';
import { WORKSPACE_PANE_LAYOUT_DEFAULTS } from './WorkspacePaneLayout.constants';
import type { WorkspacePaneLayoutProps } from './WorkspacePaneLayout.props';
import {
  WorkspacePaneLayoutCenterPane,
  WorkspacePaneLayoutPane,
  WorkspacePaneLayoutResizeHandle,
  WorkspacePaneLayoutRoot,
} from './WorkspacePaneLayout.styles';

type ResizeTarget = 'left' | 'right';

interface ResizeState {
  pointerX: number;
  target: ResizeTarget;
  width: number;
}

const clampWidth = (width: number, minWidth: number, maxWidth?: number) => {
  const nextWidth = Math.max(width, minWidth);

  return maxWidth === undefined ? nextWidth : Math.min(nextWidth, maxWidth);
};

const getNextKeyboardWidth = ({
  arrowLeftDelta,
  arrowRightDelta,
  currentWidth,
  key,
  maxWidth,
  minWidth,
}: {
  arrowLeftDelta: number;
  arrowRightDelta: number;
  currentWidth: number;
  key: string;
  maxWidth?: number;
  minWidth: number;
}) => {
  if (key === 'Home') {
    return minWidth;
  }

  if (key === 'End' && maxWidth !== undefined) {
    return maxWidth;
  }

  return currentWidth + (key === 'ArrowLeft' ? arrowLeftDelta : arrowRightDelta);
};

const WorkspacePaneLayout = ({
  center,
  defaultLeftWidth = WORKSPACE_PANE_LAYOUT_DEFAULTS.defaultLeftWidth,
  defaultRightWidth = WORKSPACE_PANE_LAYOUT_DEFAULTS.defaultRightWidth,
  left,
  maxLeftWidth,
  maxRightWidth,
  minCenterWidth = WORKSPACE_PANE_LAYOUT_DEFAULTS.minCenterWidth,
  minLeftWidth = WORKSPACE_PANE_LAYOUT_DEFAULTS.minLeftWidth,
  minRightWidth = WORKSPACE_PANE_LAYOUT_DEFAULTS.minRightWidth,
  resizeLabels,
  right,
  testIds,
}: WorkspacePaneLayoutProps) => {
  const { zoomLevel } = useZoom();
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isResettingPaneWidth, setIsResettingPaneWidth] = useState(false);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(defaultRightWidth);
  const rootRef = useRef<HTMLElement | null>(null);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const resetAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRightPane = Boolean(right);

  const clearResetAnimationTimeout = () => {
    if (resetAnimationTimeoutRef.current === null) {
      return;
    }

    clearTimeout(resetAnimationTimeoutRef.current);
    resetAnimationTimeoutRef.current = null;
  };

  const runResetAnimation = () => {
    clearResetAnimationTimeout();
    setIsResettingPaneWidth(true);
    resetAnimationTimeoutRef.current = setTimeout(() => {
      setIsResettingPaneWidth(false);
      resetAnimationTimeoutRef.current = null;
    }, WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeResetAnimationMs);
  };

  const getEffectiveMaxWidth = (target: ResizeTarget) => {
    const explicitMaxWidth = target === 'left' ? maxLeftWidth : maxRightWidth;
    const { resizeHandleWidth } = WORKSPACE_PANE_LAYOUT_DEFAULTS;

    if (!layoutWidth) {
      return explicitMaxWidth;
    }

    const reservedWidth = target === 'left'
      ? minCenterWidth + resizeHandleWidth + (hasRightPane ? rightWidth + resizeHandleWidth : 0)
      : minCenterWidth + leftWidth + (resizeHandleWidth * 2);
    const layoutMaxWidth = Math.max(
      target === 'left' ? minLeftWidth : minRightWidth,
      layoutWidth - reservedWidth
    );

    return explicitMaxWidth === undefined
      ? layoutMaxWidth
      : Math.min(explicitMaxWidth, layoutMaxWidth);
  };

  const startResize = (target: ResizeTarget, event: ReactPointerEvent<HTMLButtonElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerX: event.clientX,
      target,
      width: target === 'left' ? leftWidth : rightWidth,
    };
  };

  const stopResize = () => {
    resizeStateRef.current = null;
  };

  const resetPaneWidth = (target: ResizeTarget) => {
    stopResize();
    runResetAnimation();

    if (target === 'left') {
      setLeftWidth(clampWidth(defaultLeftWidth, minLeftWidth, getEffectiveMaxWidth('left')));
      return;
    }

    setRightWidth(clampWidth(defaultRightWidth, minRightWidth, getEffectiveMaxWidth('right')));
  };

  const handleResizeMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const resizeState = resizeStateRef.current;

    if (!resizeState) {
      return;
    }

    const delta = event.clientX - resizeState.pointerX;

    if (resizeState.target === 'left') {
      setLeftWidth(clampWidth(resizeState.width + delta, minLeftWidth, getEffectiveMaxWidth('left')));
      return;
    }

    setRightWidth(clampWidth(resizeState.width - delta, minRightWidth, getEffectiveMaxWidth('right')));
  };

  const handleResizeKeyDown =
    (target: ResizeTarget) => (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        return;
      }

      event.preventDefault();

      if (target === 'left') {
        const nextLeftWidth = getNextKeyboardWidth({
          arrowLeftDelta: -WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeStep,
          arrowRightDelta: WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeStep,
          currentWidth: leftWidth,
          key: event.key,
          maxWidth: getEffectiveMaxWidth('left'),
          minWidth: minLeftWidth,
        });
        setLeftWidth(clampWidth(nextLeftWidth, minLeftWidth, getEffectiveMaxWidth('left')));
        return;
      }

      const nextRightWidth = getNextKeyboardWidth({
        arrowLeftDelta: WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeStep,
        arrowRightDelta: -WORKSPACE_PANE_LAYOUT_DEFAULTS.resizeStep,
        currentWidth: rightWidth,
        key: event.key,
        maxWidth: getEffectiveMaxWidth('right'),
        minWidth: minRightWidth,
      });
      setRightWidth(clampWidth(nextRightWidth, minRightWidth, getEffectiveMaxWidth('right')));
    };

  // Pane resize maxes are derived from the live container so pages are not capped by arbitrary constants.
  useEffect(() => {
    const rootNode = rootRef.current;

    if (!rootNode) {
      return undefined;
    }

    const updateLayoutWidth = () => {
      const nextLayoutWidth = rootNode.getBoundingClientRect().width;

      setLayoutWidth(currentLayoutWidth => (
        currentLayoutWidth === nextLayoutWidth ? currentLayoutWidth : nextLayoutWidth
      ));
    };

    if (typeof ResizeObserver === 'undefined') {
      updateLayoutWidth();
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateLayoutWidth);

    updateLayoutWidth();
    resizeObserver.observe(rootNode);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => () => {
    if (resetAnimationTimeoutRef.current !== null) {
      clearTimeout(resetAnimationTimeoutRef.current);
    }
  }, []);

  // Reset internal pane widths when a story/page intentionally changes its defaults.
  useEffect(() => {
    setLeftWidth(defaultLeftWidth);
  }, [defaultLeftWidth]);

  // Reset internal pane widths when a story/page intentionally changes its defaults.
  useEffect(() => {
    setRightWidth(defaultRightWidth);
  }, [defaultRightWidth]);

  return (
    <PrismThemeProvider>
      <WorkspacePaneLayoutRoot
        ref={rootRef}
        centerMinWidth={minCenterWidth}
        data-testid={testIds?.root}
        hasRightPane={hasRightPane}
        isResettingPaneWidth={isResettingPaneWidth}
        leftWidth={leftWidth}
        rightWidth={hasRightPane ? rightWidth : 0}
        zoomLevel={zoomLevel}>
        <WorkspacePaneLayoutPane data-testid={testIds?.leftPane}>{left}</WorkspacePaneLayoutPane>
        <WorkspacePaneLayoutResizeHandle
          aria-label={resizeLabels?.left ?? WORKSPACE_PANE_LAYOUT_DEFAULTS.leftResizeLabel}
          aria-orientation="vertical"
          aria-valuemax={getEffectiveMaxWidth('left')}
          aria-valuemin={minLeftWidth}
          aria-valuenow={leftWidth}
          data-testid={testIds?.leftResizeHandle}
          onKeyDown={handleResizeKeyDown('left')}
          onDoubleClick={() => resetPaneWidth('left')}
          onPointerCancel={stopResize}
          onPointerDown={event => startResize('left', event)}
          onPointerMove={handleResizeMove}
          onPointerUp={stopResize}
          role="separator"
          type="button"
        />
        <WorkspacePaneLayoutCenterPane data-testid={testIds?.centerPane}>{center}</WorkspacePaneLayoutCenterPane>
        {hasRightPane ? (
          <>
            <WorkspacePaneLayoutResizeHandle
              aria-label={resizeLabels?.right ?? WORKSPACE_PANE_LAYOUT_DEFAULTS.rightResizeLabel}
              aria-orientation="vertical"
              aria-valuemax={getEffectiveMaxWidth('right')}
              aria-valuemin={minRightWidth}
              aria-valuenow={rightWidth}
              data-testid={testIds?.rightResizeHandle}
              onKeyDown={handleResizeKeyDown('right')}
              onDoubleClick={() => resetPaneWidth('right')}
              onPointerCancel={stopResize}
              onPointerDown={event => startResize('right', event)}
              onPointerMove={handleResizeMove}
              onPointerUp={stopResize}
              role="separator"
              type="button"
            />
            <WorkspacePaneLayoutPane data-testid={testIds?.rightPane}>{right}</WorkspacePaneLayoutPane>
          </>
        ) : null}
      </WorkspacePaneLayoutRoot>
    </PrismThemeProvider>
  );
};

export default WorkspacePaneLayout;
